/**
 * MappingService.js
 * 
 * Service für die Verwaltung von Mapping-Tabellen.
 * Ermöglicht das Erstellen von Dimension-Tabellen basierend auf unique values.
 */

class MappingService {
  /**
   * Holt den Auth-Token vom Supabase Client
   * @returns {Promise<string>} - Auth Token
   */
  static async getAuthToken() {
    if (typeof window !== 'undefined' && window.supabase) {
      const { data } = await window.supabase.auth.getSession();
      return data?.session?.access_token || null;
    }
    return null;
  }

  static async buildAuthHeaders(baseHeaders = {}) {
    const token = await this.getAuthToken();
    const headers = { ...baseHeaders };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }

  /**
   * Erstellt eine neue Mapping-Tabelle
   * @param {string} organizationId - UUID der Organisation
   * @param {string} sourceTableId - UUID der Source-Tabelle
   * @param {string} sourceColumnName - Name der Spalte für Mapping
   * @returns {Promise<Object>} - Mapping-Info inkl. Tabellennamen
   */
  static async createMapping(organizationId, sourceTableId, sourceColumnName) {
    try {
      
      // Hole Auth-Token
      const token = await this.getAuthToken();
      if (!token) {
        throw new Error('Keine Authentifizierung gefunden. Bitte melde dich an.');
      }
      
      const response = await fetch('/api/mappings/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          organization_id: organizationId,
          source_table_id: sourceTableId,
          source_column_name: sourceColumnName
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        console.error('❌ Server-Antwort:', error);
        throw new Error(error.details || error.error || 'Fehler beim Erstellen des Mappings');
      }
      
      const data = await response.json();
      return data;
      
    } catch (error) {
      console.error('❌ Fehler in createMapping:', error);
      throw error;
    }
  }
  
  /**
   * Findet kompatible Mapping-Tabellen für eine Spalte
   * @param {string} organizationId - UUID der Organisation
   * @param {string} sourceColumnName - Name der Spalte
   * @returns {Promise<Array>} - Liste kompatibler Mappings
   */
  static async getCompatibleMappings(organizationId, sourceColumnName) {
    try {
      
      const { data, error } = await window.supabase.rpc('get_compatible_mappings', {
        p_organization_id: organizationId,
        p_source_column_name: sourceColumnName
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      return data || [];
      
    } catch (error) {
      console.error('❌ Fehler in getCompatibleMappings:', error);
      // Wir werfen hier keinen Fehler, sondern geben leeres Array zurück,
      // damit der Workflow nicht blockiert wird
      return [];
    }
  }

  /**
   * Verknüpft eine Tabelle mit einer existierenden Mapping-Tabelle
   * @param {string} organizationId - UUID der Organisation
   * @param {string} sourceTableId - UUID der Source-Tabelle
   * @param {string} sourceColumnName - Name der Spalte
   * @param {string} existingMappingTableName - Name der existierenden Mapping-Tabelle
   * @returns {Promise<Object>} - Ergebnis
   */
  static async linkTableToMapping(organizationId, sourceTableId, sourceColumnName, existingMappingTableName) {
    try {
      
      const { data, error } = await window.supabase.rpc('link_table_to_mapping', {
        p_organization_id: organizationId,
        p_source_table_id: sourceTableId,
        p_source_column_name: sourceColumnName,
        p_existing_mapping_table_name: existingMappingTableName
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      return data;
      
    } catch (error) {
      console.error('❌ Fehler in linkTableToMapping:', error);
      throw error;
    }
  }

  /**
   * Holt alle Mappings einer Organisation aus new_uploaded_tables.mapped_columns
   * @param {string} organizationId - UUID der Organisation
   * @returns {Promise<Array>} - Liste aller Mappings mit Quelltabellen-Info
   */
  static async getMappings(organizationId = null) {
    try {
      if (!organizationId) {
        console.warn('⚠️ Keine organizationId angegeben');
        return [];
      }


      // Hole alle Tabellen mit ihren Mappings
      const { data: tables, error } = await window.supabase
        .from('new_uploaded_tables')
        .select('id, name, table_name, mapped_columns, created_at')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      // Transformiere die Daten in das erwartete Format
      const mappings = [];
      
      for (const table of tables) {
        const mappedColumns = table.mapped_columns || [];
        
        // Jedes Mapping in ein separates Objekt umwandeln
        for (const mapping of mappedColumns) {
          // Verwende 'id' falls vorhanden, sonst 'mapping_id' (für Kompatibilität)
          const mappingId = mapping.id || mapping.mapping_id;
          
          mappings.push({
            id: mappingId,
            mapping_id: mapping.mapping_id, // Behalte auch mapping_id für Kompatibilität
            source_table_id: table.id,
            source_table_name: table.table_name,
            source_table_display_name: table.name,
            source_column_name: mapping.column_name,
            mapping_table_name: mapping.mapping_table_name,
            unique_values_count: mapping.unique_values_count,
            created_at: mapping.created_at,
            created_by: mapping.created_by,
            // Für Kompatibilität mit alter Struktur
            new_uploaded_tables: {
              name: table.name,
              table_name: table.table_name
            }
          });
        }
      }

      return mappings;
      
    } catch (error) {
      console.error('❌ Fehler in getMappings:', error);
      throw error;
    }
  }
  
  /**
   * Holt die Daten einer Mapping-Tabelle
   * @param {string} mappingTableName - Name der Mapping-Tabelle
   * @returns {Promise<Object>} - Mapping-Info und Daten
   */
  static async getMappingData(mappingTableName) {
    try {
      const headers = await this.buildAuthHeaders();
      
      const response = await fetch(`/api/mappings/${mappingTableName}/data`, {
        headers
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Abrufen der Mapping-Daten');
      }
      
      const result = await response.json();
      return result;
      
    } catch (error) {
      console.error('❌ Fehler in getMappingData:', error);
      throw error;
    }
  }
  
  /**
   * Fügt eine neue Spalte zu einer Mapping-Tabelle hinzu
   * @param {string} mappingTableName - Name der Mapping-Tabelle
   * @param {string} columnName - Name der neuen Spalte
   * @param {string} columnType - Datentyp (TEXT, INTEGER, NUMERIC, BOOLEAN, DATE, TIMESTAMPTZ)
   * @returns {Promise<Object>} - Erfolgsinfo
   */
  static async addColumn(mappingTableName, columnName, columnType = 'TEXT') {
    try {
      const headers = await this.buildAuthHeaders({
        'Content-Type': 'application/json'
      });
      
      const response = await fetch(`/api/mappings/${mappingTableName}/columns`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          column_name: columnName,
          column_type: columnType
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Hinzufügen der Spalte');
      }
      
      const data = await response.json();
      return data;
      
    } catch (error) {
      console.error('❌ Fehler in addColumn:', error);
      throw error;
    }
  }
  
  /**
   * Aktualisiert einen Mapping-Wert
   * @param {string} mappingTableName - Name der Mapping-Tabelle
   * @param {string} mappingValue - Der Wert (Primary Key)
   * @param {Object} updates - Objekt mit Spalte: Wert Paaren
   * @returns {Promise<Object>} - Aktualisierte Daten
   */
  static async updateMappingValue(mappingTableName, mappingValue, updates) {
    try {
      const headers = await this.buildAuthHeaders({
        'Content-Type': 'application/json'
      });
      
      const response = await fetch(`/api/mappings/${mappingTableName}/data`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          mapping_value: mappingValue,
          updates: updates
        })
      });

      const rawBody = await response.text();
      let parsedBody = null;
      if (rawBody) {
        try {
          parsedBody = JSON.parse(rawBody);
        } catch (_) {
          parsedBody = null;
        }
      }

      if (!response.ok) {
        const message = parsedBody?.error || parsedBody?.message || rawBody || `HTTP ${response.status}`;
        throw new Error(message || 'Fehler beim Aktualisieren');
      }

      return parsedBody || [];
      
    } catch (error) {
      console.error('❌ Fehler in updateMappingValue:', error);
      throw error;
    }
  }

  /**
   * Aktualisiert mehrere Mapping-Werte in einem Batch
   * @param {string} mappingTableName - Name der Mapping-Tabelle
   * @param {Array<{mapping_value: string, updates: Object}>} rows - Batch-Updates
   * @param {{replaceAll?: boolean, replaceColumns?: string[]}} options - Optionale Replace-All Steuerung
   * @returns {Promise<Object>} - Batch-Ergebnis
   */
  static async updateMappingValuesBatch(mappingTableName, rows = [], options = {}) {
    try {
      const headers = await this.buildAuthHeaders({
        'Content-Type': 'application/json'
      });

      const payload = {
        rows: Array.isArray(rows) ? rows : []
      };
      if (options?.replaceAll === true) {
        payload.replace_all = true;
      }
      if (Array.isArray(options?.replaceColumns) && options.replaceColumns.length > 0) {
        payload.replace_columns = options.replaceColumns;
      }

      const response = await fetch(`/api/mappings/${mappingTableName}/data/batch`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(payload)
      });
      const rawBody = await response.text();
      let parsedBody = null;
      if (rawBody) {
        try {
          parsedBody = JSON.parse(rawBody);
        } catch (_) {
          parsedBody = null;
        }
      }

      if (!response.ok) {
        const message = parsedBody?.error || parsedBody?.message || rawBody || `HTTP ${response.status}`;
        throw new Error(message || 'Fehler beim Batch-Aktualisieren');
      }

      return parsedBody || { success: true, results: [] };
    } catch (error) {
      console.error('❌ Fehler in updateMappingValuesBatch:', error);
      throw error;
    }
  }

  /**
   * Generiert KI-Vorschläge für ausgewählte Mapping-Werte
   * @param {string} mappingTableName - Name der Mapping-Tabelle
   * @param {{mapping_values: string[], target_columns?: string[], options?: Object}} payload - Anfrage-Payload
   * @returns {Promise<Object>} - Vorschlags-Ergebnis
   */
  static async getAiSuggestions(mappingTableName, payload = {}) {
    try {
      const headers = await this.buildAuthHeaders({
        'Content-Type': 'application/json'
      });

      const response = await fetch(`/api/mappings/${encodeURIComponent(mappingTableName)}/suggestions`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      const rawBody = await response.text();
      let parsedBody = null;
      if (rawBody) {
        try {
          parsedBody = JSON.parse(rawBody);
        } catch (_) {
          parsedBody = null;
        }
      }

      if (!response.ok) {
        const details = parsedBody?.details ? String(parsedBody.details).trim() : '';
        const baseMessage = parsedBody?.error || parsedBody?.message || rawBody || `HTTP ${response.status}`;
        const proxyHint = response.status === 502 && !details
          ? 'Lokales Backend auf http://localhost:3000 ist nicht erreichbar. Starte `npm run dev:server` oder `npm run dev:stack`.'
          : '';
        const composedMessage = [baseMessage, details || proxyHint].filter(Boolean).join(': ');
        throw new Error(composedMessage || 'Fehler beim Generieren der KI-Vorschläge');
      }

      return parsedBody || { success: true, suggestions: [] };
    } catch (error) {
      console.error('❌ Fehler in getAiSuggestions:', error);
      throw error;
    }
  }
  
  /**
   * Lädt die letzten N Buchungen für ein Konto aus der Quelltabelle der Mapping-Tabelle.
   * @param {string} mappingTableName
   * @param {string} konto
   * @param {{limit?: number, redacted?: boolean}} options
   * @returns {Promise<{konto: string, account_name: string|null, saldo_recent_sum: number|null, redacted: boolean, bookings: Array}>}
   */
  static async getRecentBookings(mappingTableName, konto, options = {}) {
    try {
      const headers = await this.buildAuthHeaders();
      const params = new URLSearchParams();
      if (options.limit) params.set('limit', String(options.limit));
      if (options.redacted) params.set('redacted', 'true');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const url = `/api/mappings/${encodeURIComponent(mappingTableName)}/recent-bookings/${encodeURIComponent(konto)}${qs}`;

      const response = await fetch(url, { method: 'GET', headers });
      const rawBody = await response.text();
      let parsedBody = null;
      try { parsedBody = rawBody ? JSON.parse(rawBody) : null; } catch (_) { parsedBody = null; }

      if (!response.ok) {
        const message = parsedBody?.error || `HTTP ${response.status}`;
        throw new Error(message);
      }
      return parsedBody || { konto, account_name: null, saldo_recent_sum: null, redacted: !!options.redacted, bookings: [] };
    } catch (error) {
      console.error('❌ Fehler in getRecentBookings:', error);
      throw error;
    }
  }

  /**
   * Löscht ein Mapping komplett
   * @param {string} mappingId - UUID des Mappings
   * @returns {Promise<Object>} - Erfolgsinfo
   */
  static async deleteMapping(mappingId) {
    try {
      const headers = await this.buildAuthHeaders();
      
      const response = await fetch(`/api/mappings/${mappingId}`, {
        method: 'DELETE',
        headers
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Löschen des Mappings');
      }
      
      const data = await response.json();
      return data;
      
    } catch (error) {
      console.error('❌ Fehler in deleteMapping:', error);
      throw error;
    }
  }
  
  /**
   * Holt die Spalten-Struktur einer Mapping-Tabelle
   * Analysiert die Daten um herauszufinden welche Spalten existieren
   * @param {string} mappingTableName - Name der Mapping-Tabelle
   * @returns {Promise<Array>} - Liste der Spalten
   */
  static async getMappingColumns(mappingTableName) {
    try {
      const result = await this.getMappingData(mappingTableName);
      
      if (!result.data || result.data.length === 0) {
        return ['mapping_value']; // Mindestens die Primary Key Spalte
      }
      
      // Extrahiere Spaltennamen aus dem ersten Datensatz
      const columns = Object.keys(result.data[0]);
      return columns;
      
    } catch (error) {
      console.error('❌ Fehler in getMappingColumns:', error);
      throw error;
    }
  }
}

export { MappingService };
